import * as preact from "preact";
import * as _ from "underscore";

import * as analytics from "analytics";
import LatestRequest from "LatestRequest";
import { shownAgain } from "pages/PreactPage";
import readlangClient from "readlangClient";
import * as dateUtils from "shared/dateUtils";
import * as utils from "shared/utils";
import BookStore from "stores/BookStore";
import stores from "stores/stores";
import * as tts from "tts";
import BookCoverConnector from "views/BookCoverConnector";
import { EmailReminderAndStreakRepair } from "views/EmailReminderAndStreakRepairView";
import PracticeButton from "views/PracticeButton";
import SkeletonView from "views/SkeletonView";
import WalkthroughChecklistView from "views/WalkthroughChecklistView";
import * as walkthrough from "walkthrough";

const TRUSTPILOT_REVIEW_URL =
  "https://www.trustpilot.com/evaluate/readlang.com";

interface Props {
  active?: boolean;
  appRouter: any;
  options?: object;
  reselections?: number;
}

interface DashboardData {
  showPracticeGraph?: boolean;
  streak?: {
    days: number;
    plural?: boolean;
  };
  karma?: {
    booksShared: number;
    booksSharedPlural?: boolean;
    karmaScore?: number;
  };
  conversationButton?: boolean;
  flashcardOrConversation?: boolean;
  flashcardReminder?: boolean;
  showRow?: boolean;
  practiceUrl?: string;
  speakingSupported?: boolean;
  continueReading?: any;
  newStreak?: {
    streak: number;
    goalMetToday: boolean;
    heading?: string;
    recentStats: Array<{
      date: string;
      dayOfWeek: string;
      wordsRead: number;
      wordsPracticed: number;
      streakRepair: number;
      practiceWordsReadEquivalent: number;
      wordsPerDayGoal: number;
      goalMet: boolean;
      wordsReadHeight?: number;
      wordsPracticedHeight?: number;
      wordsPerDayGoalHeight?: number;
      streakRepairHeight?: number;
      wasRepaired?: boolean;
      emptyHeight?: number;
      dateString?: string;
      rightHandSide?: boolean;
    }>;
    recentStatsExist?: boolean;
    wordsPerDayGoalHeight?: number;
  };
  showForumCallout?: boolean;
  showTrustpilotCallout?: boolean;
  user?: any;
}

type State =
  | {
      type: "fetching";
    }
  | {
      type: "fetched";
      data: DashboardData;
    };

class DashboardPageView extends preact.Component<Props, State> {
  private disposeLanguageWatch?: () => void;
  private readonly latestFetch = new LatestRequest();

  constructor(props: Props) {
    super(props);
    this.state = {
      type: "fetching",
    };

    this.fetchData();

    this.disposeLanguageWatch = stores.user.onChange(
      (a) => a.currentLearningLanguage,
      this.onLanguageChange
    );
  }

  componentDidUpdate(previousProps: Props) {
    // Back from another page (the tab kept as left), perhaps after reading,
    // or its tab reselected
    if (shownAgain(this.props, previousProps)) {
      void this.fetchData();
    }
  }

  onLanguageChange = async () => {
    await this.setState({
      type: "fetching",
    });
    this.fetchData();
  };

  async fetchData() {
    localStorage.setItem("libraryOrDashboardPath", "/dashboard");

    // Fetch dashboard data
    const response = await this.latestFetch.send(
      readlangClient.request("GET", "/api/dashboard", {
        timezoneOffset: dateUtils.getTimezoneOffset(),
        language: stores.user.currentLearningLanguage.value,
      })
    );
    if (!response) {
      return;
    }
    const dashboardData: DashboardData = response.data;

    // Process dashboard data
    if (walkthrough.hasAchievement("translate")) {
      dashboardData.showPracticeGraph = true;
    }

    if (dashboardData.streak && dashboardData.streak.days > 1) {
      dashboardData.streak.plural = true;
    }

    if (dashboardData.karma) {
      dashboardData.karma.booksSharedPlural =
        dashboardData.karma.booksShared !== 1;
    }

    if (stores.user.hasChatFeature.value) {
      dashboardData.conversationButton = true;
    }

    dashboardData.flashcardOrConversation =
      dashboardData.flashcardReminder || dashboardData.conversationButton;

    dashboardData.showRow =
      dashboardData.continueReading || dashboardData.flashcardOrConversation
        ? true
        : false;
    dashboardData.practiceUrl = "/flashcards";

    // Initialize browser voices before checking availability so free users
    // on a language with installed device voices still see the Speaking row
    // (availability is tier-aware and consults the browser voice cache).
    await tts.initialize(stores.user.currentLearningLanguage.value);
    if (
      tts.getAvailabilitySignal({
        user: stores.user,
        feature: "speakingMode",
        language: stores.user.currentLearningLanguage.value,
      }).value === "supported"
    ) {
      dashboardData.speakingSupported = true;
    }

    if (dashboardData.newStreak) {
      if (dashboardData.newStreak.streak === 0) {
        dashboardData.newStreak.heading = `Start a streak`;
      } else if (dashboardData.newStreak.goalMetToday) {
        dashboardData.newStreak.heading = `You have ${utils.aOrAn(
          dashboardData.newStreak.streak
        )} ${dashboardData.newStreak.streak} day streak${
          dashboardData.newStreak.streak > 9 ? "!" : ""
        }`;
      } else if (new Date().getHours() < 17) {
        dashboardData.newStreak.heading = `Keep up your ${dashboardData.newStreak.streak} day streak`;
      } else {
        dashboardData.newStreak.heading = `Your ${dashboardData.newStreak.streak} day streak is in danger`;
      }

      const maxWordsReadEquivalent = _.max(
        dashboardData.newStreak.recentStats.map(
          (recentDayStats) =>
            recentDayStats.wordsPracticed *
              recentDayStats.practiceWordsReadEquivalent +
            recentDayStats.wordsRead
        )
      );

      dashboardData.newStreak.recentStatsExist = maxWordsReadEquivalent > 0;

      let maxYAxis = _.max([
        maxWordsReadEquivalent,
        ...dashboardData.newStreak.recentStats.map(
          (recentDayStats) => recentDayStats.wordsPerDayGoal
        ),
      ]);

      // Round up to nearest 250 words
      maxYAxis = Math.ceil(maxYAxis / 250) * 250;

      for (const [
        index,
        recentDayStats,
      ] of dashboardData.newStreak.recentStats.entries()) {
        recentDayStats.wordsReadHeight =
          (recentDayStats.wordsRead / maxYAxis) * 100;
        recentDayStats.wordsPracticedHeight =
          ((recentDayStats.wordsPracticed *
            recentDayStats.practiceWordsReadEquivalent) /
            maxYAxis) *
          100;
        recentDayStats.wordsPerDayGoalHeight =
          (recentDayStats.wordsPerDayGoal / maxYAxis) * 100 - 1;

        // A day whose goal was only met via a streak repair: its own reading and
        // practice fell short of the goal. Show an orange bar filling the gap from
        // the top of the earned bars up to the goal line.
        const earnedEquivalent =
          recentDayStats.wordsRead +
          recentDayStats.wordsPracticed *
            recentDayStats.practiceWordsReadEquivalent;
        recentDayStats.wasRepaired =
          recentDayStats.streakRepair > 0 &&
          earnedEquivalent < recentDayStats.wordsPerDayGoal;
        recentDayStats.streakRepairHeight = recentDayStats.wasRepaired
          ? Math.max(
              0,
              recentDayStats.wordsPerDayGoalHeight -
                recentDayStats.wordsReadHeight -
                recentDayStats.wordsPracticedHeight
            )
          : 0;

        recentDayStats.emptyHeight =
          100 -
          recentDayStats.wordsReadHeight -
          recentDayStats.wordsPracticedHeight -
          recentDayStats.streakRepairHeight;

        // It's important to use `/` instead of `-` because of the way that JavaScript handles
        // dates. If we use `-`, then the date will be interpreted as UTC, and the date will be
        // off by one day.
        const date = new Date(recentDayStats.date.replace(/-/g, "/"));
        recentDayStats.dateString = `${dateUtils.dayOfWeekString(
          date
        )} ${date.getDate()} ${dateUtils.monthString(date)}`;

        recentDayStats.rightHandSide = index > 3;
      }

      // XXX Making the assumption that the goal is the same every day.
      dashboardData.newStreak.wordsPerDayGoalHeight =
        dashboardData.newStreak.recentStats[0].wordsPerDayGoalHeight;
    }

    dashboardData.showForumCallout =
      !(stores.user.state.value.forumAccess != null) &&
      stores.user.hasPremiumAccess.value;

    dashboardData.user = stores.user.viewJSON.value;

    this.setState({
      type: "fetched",
      data: dashboardData,
    });

    if (dashboardData.showTrustpilotCallout) {
      analytics.trackEvent("showTrustpilotCallout");
    }
  }

  componentWillUnmount() {
    this.disposeLanguageWatch?.();
  }

  onTrustpilotCalloutClick = () => {
    readlangClient.request("POST", "/api/user/trustpilot-callout", {
      action: "click",
    });
    this.hideTrustpilotCallout();
  };

  onTrustpilotCalloutDismiss = (event: Event) => {
    event.preventDefault();
    readlangClient.request("POST", "/api/user/trustpilot-callout", {
      action: "dismiss",
    });
    this.hideTrustpilotCallout();
  };

  hideTrustpilotCallout = () => {
    if (this.state.type !== "fetched") return;
    this.setState({
      type: "fetched",
      data: { ...this.state.data, showTrustpilotCallout: false },
    });
  };

  render() {
    if (this.state.type === "fetching") {
      return <SkeletonView kind="blocks" count={3} />;
    }

    const { data } = this.state;

    return (
      <div>
        <WalkthroughChecklistView
          stores={stores}
          appRouter={this.props.appRouter}
        />

        {data.showForumCallout && (
          <div class="forumCallout callout">
            Say hello, suggest features, report bugs and join discussions on the{" "}
            <a href="https://forum.readlang.com" target="_blank">
              Readlang Forum
            </a>
            .
          </div>
        )}

        {data.showTrustpilotCallout && (
          <div class="trustpilotCallout callout">
            <span class="message">
              Enjoying Readlang? It would mean a lot if you'd take a moment to{" "}
              <a
                href={TRUSTPILOT_REVIEW_URL}
                target="_blank"
                rel="noopener"
                onClick={this.onTrustpilotCalloutClick}
              >
                leave a quick review on Trustpilot
              </a>
              . It really helps other learners find us. 🙏
            </span>
            <button
              type="button"
              class="closeButton"
              aria-label="Dismiss"
              onClick={this.onTrustpilotCalloutDismiss}
            >
              <i class="icon-remove"></i>
            </button>
          </div>
        )}

        {data.showRow && (
          <div class="dashboardRow">
            {data.continueReading && (
              <div class="dashboardPanel continueReading">
                <h2 style="margin-bottom:15px;">Continue reading</h2>
                <p>
                  <div class="bookCover">
                    <BookCoverConnector
                      book={new BookStore(data.continueReading)}
                      userID={stores.user.id.value as string}
                      compactMode={true}
                    />
                  </div>
                </p>
                <p class="moreLink">
                  <a href="/myTexts">Manage my queue</a>
                </p>
              </div>
            )}

            {data.flashcardOrConversation && (
              <div class="dashboardPanel practiceFlashcards">
                <h2>Practice</h2>

                {data.flashcardReminder && (
                  <PracticeButton
                    href={data.practiceUrl}
                    icon="/content/flashcardsIcon-v2.svg"
                    title="Start practice"
                  />
                )}
                {data.conversationButton && (
                  <PracticeButton
                    href="/chat/start"
                    icon="/content/chat-v3.svg"
                    title="Start a conversation"
                  />
                )}
                {data.flashcardReminder && (
                  <p class="moreLink">
                    <a href="/learn">Customize practice</a>
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {data.newStreak && (
          <div class="dashboardPanel" style="text-align:center">
            <h2>{data.newStreak.heading}</h2>
            <p>
              Read 500 words or practice 10 flashcards every day to keep your
              streak going.
            </p>
            <div class="streakPanelContainer">
              <div class="streakPanel">
                {data.newStreak.recentStatsExist && (
                  <div class="barChartLabels">
                    <div class="barChartLabel">
                      <span class="wordsReadLabelSquare"></span>
                      <span class="label">
                        Words <br class="mobileOnly" />
                        Read
                      </span>
                    </div>
                    <div class="barChartLabel">
                      <span class="wordsPracticedLabelSquare"></span>
                      <span class="label">
                        Words <br class="mobileOnly" />
                        Practiced
                      </span>
                    </div>
                    <div class="barChartLabel">
                      <span class="goalLineLabelExample"></span>
                      <span class="label">
                        Daily <br class="mobileOnly" />
                        Goal
                      </span>
                    </div>
                  </div>
                )}
                <div class="barChart">
                  {data.newStreak.recentStatsExist && (
                    <>
                      <div
                        class="goalLine"
                        style={`bottom: ${data.newStreak.wordsPerDayGoalHeight}px`}
                      ></div>
                      <div class="bottomLine"></div>
                      <table>
                        <tr>
                          {data.newStreak.recentStats.map((stat) => (
                            <td class="barCell" key={stat.date}>
                              <div
                                class="emptyBar"
                                style={`height: ${stat.emptyHeight}px`}
                              ></div>
                              {stat.wasRepaired && (
                                <div
                                  class="streakRepairBar"
                                  style={`height: ${stat.streakRepairHeight}px`}
                                ></div>
                              )}
                              <div
                                class="wordsPracticedBar"
                                style={`height: ${stat.wordsPracticedHeight}px`}
                              ></div>
                              <div
                                class="wordsReadBar"
                                style={`height: ${stat.wordsReadHeight}px`}
                              ></div>
                              <div
                                class={`barCellTooltip${
                                  stat.rightHandSide ? " right" : ""
                                }`}
                              >
                                <h3>{stat.dateString}</h3>
                                <div class="content">
                                  Words Read: {stat.wordsRead}
                                  <br />
                                  Words Practiced: {stat.wordsPracticed}
                                  {stat.wasRepaired && (
                                    <div class="streakRepairedNote">
                                      Streak repaired
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                          ))}
                        </tr>
                      </table>
                    </>
                  )}
                </div>
                <table>
                  <tr>
                    {data.newStreak.recentStats.map((stat) => (
                      <th key={`${stat.date}-day`}>{stat.dayOfWeek}</th>
                    ))}
                  </tr>
                  <tr>
                    {data.newStreak.recentStats.map((stat) => (
                      <td key={`${stat.date}-fire`}>
                        <img
                          class={`fireIcon ${stat.goalMet ? "goalMet" : ""}`}
                          src="/content/fire-icon.svg"
                        />
                      </td>
                    ))}
                  </tr>
                </table>
              </div>
            </div>
            <p class="moreLink">
              <a href="/progress">Show long-term progress</a>
            </p>
            <div id="emailReminderOptIn">
              <EmailReminderAndStreakRepair stores={stores} />
            </div>
          </div>
        )}

        {data.karma && (
          <div class="dashboardPanel">
            <h2>
              {data.karma.karmaScore ? (
                <>Your karma score is {data.karma.karmaScore}</>
              ) : (
                <>Earn karma by sharing texts with the community</>
              )}
            </h2>
            <p class="panelBodyText">
              You have a karma score of {data.karma.karmaScore} from the{" "}
              {data.karma.booksShared}{" "}
              {data.user.currentLearningLanguageEnglishString} text
              {data.karma.booksSharedPlural ? "s" : ""} you shared. Your karma
              score will increase every time another user likes one of your
              shared texts.
            </p>
          </div>
        )}
      </div>
    );
  }
}

export default DashboardPageView;
